class CartEditDrawer extends HTMLElement {
  constructor() {
    super();
    this.currentLine = null;
    this.currentVariantId = null;
    this.productUrl = null;
    this.dimensionsData = null;
    this.variantsSkuData = null;
    this.variantGalleries = null;
    this.productMediaFallback = null;
    this.editSectionId = null;
    this.variantChangeUnsubscriber = null;
    this.dimensionsObserver = null;
    this.loadAbortController = null;
    this.openRequestId = 0;
    this.contentElement = this.querySelector('#CartEditDrawer-Content');
    this.overlay = this.querySelector('#CartEditDrawer-Overlay');

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    if (this.overlay) {
      this.overlay.addEventListener('click', this.close.bind(this));
    }

    // Handle edit button clicks
    this.setupEditButtons();
  }

  setupEditButtons() {
    document.addEventListener('click', (event) => {
      const editButton = event.target.closest('.cart-item__edit');
      if (editButton) {
        event.preventDefault();
        const line = editButton.dataset.editItem;
        const variantId = editButton.dataset.variantId;
        const productUrl = editButton.dataset.productUrl;
        this.open(line, variantId, productUrl, editButton);
      }
    });
  }

  open(line, variantId, productUrl, triggeredBy) {
    this.currentLine = line;
    this.currentVariantId = variantId;
    this.productUrl = productUrl;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.openRequestId += 1;
    const requestId = this.openRequestId;

    if (this.loadAbortController) {
      this.loadAbortController.abort();
    }
    this.loadAbortController = new AbortController();

    this.teardownVariantSync();
    this.contentElement.replaceChildren();

    fetch(productUrl, { signal: this.loadAbortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (requestId !== this.openRequestId) return;

        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.cacheMediaDataFromDocument(responseHTML);

        const productElement = responseHTML.querySelector('product-info');

        if (!productElement) {
          this.showLoadError('Unable to load product information.', requestId);
          return;
        }

        const editHTML = this.preprocessHTML(productElement);
        if (!editHTML) {
          this.showLoadError('Unable to load product information.', requestId);
          return;
        }

        this.contentElement.replaceChildren();
        this.contentElement.appendChild(editHTML.cloneNode(true));

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        this.setupUpdateButton();
        this.setupVariantSync();
        this.setCardImage(this.getVariantImageUrl(this.currentVariantId));

        setTimeout(() => {
          if (requestId !== this.openRequestId) return;
          this.classList.add('active');
          const focusElement = this.querySelector('.cart-edit-drawer__close') || this.querySelector('input, button');
          if (focusElement) trapFocus(this, focusElement);
        });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        if (requestId !== this.openRequestId) return;
        console.error('Error loading product:', error);
        this.showLoadError('Error loading product information.', requestId);
      });
  }

  showLoadError(message, requestId) {
    if (requestId != null && requestId !== this.openRequestId) return;

    this.contentElement.replaceChildren();
    const errorParagraph = document.createElement('p');
    errorParagraph.className = 'cart-edit-drawer__error';
    errorParagraph.setAttribute('role', 'alert');
    errorParagraph.textContent = message;
    this.contentElement.appendChild(errorParagraph);
    this.classList.add('active');

    const dialog = this.querySelector('.cart-edit-drawer__content');
    if (dialog) {
      if (!dialog.getAttribute('role')) dialog.setAttribute('role', 'dialog');
      if (!dialog.hasAttribute('aria-modal')) dialog.setAttribute('aria-modal', 'true');
      if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
    }

    const focusElement = this.querySelector('.cart-edit-drawer__close');
    if (focusElement && typeof trapFocus === 'function') {
      trapFocus(this, focusElement);
    }
  }

  parseJsonFromScript(root, selector) {
    const script = root?.querySelector(selector);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error(`Failed to parse JSON from ${selector}`, e);
      return null;
    }
  }

  cacheMediaDataFromDocument(doc) {
    this.variantGalleries = this.parseJsonFromScript(doc, '[data-variant-galleries]');
    this.productMediaFallback = this.parseJsonFromScript(doc, '[data-product-media-fallback]');
    this.cacheVariantsSkuData(doc);
  }

  cacheVariantsSkuData(root) {
    const variants = this.parseJsonFromScript(root, '[data-product-variants]');
    this.variantsSkuData = {};

    if (!Array.isArray(variants)) return;

    variants.forEach((variant) => {
      if (variant?.id == null || variant.sku == null) return;
      const sku = String(variant.sku).trim();
      if (sku) this.variantsSkuData[String(variant.id)] = sku;
    });
  }

  resolveVariantSku(variant, html) {
    if (!variant) return '';

    if (variant.sku != null) {
      const skuFromVariant = String(variant.sku).trim();
      if (skuFromVariant) return skuFromVariant;
    }

    const cachedSku = this.variantsSkuData?.[String(variant.id)];
    if (cachedSku) return cachedSku;

    const skuSource = html?.getElementById?.(`Sku-${this.editSectionId}`);
    if (skuSource) {
      const skuClone = skuSource.cloneNode(true);
      skuClone.querySelector('.product__sku-label')?.remove();
      const skuFromHtml = (skuClone.textContent || '').trim();
      if (skuFromHtml) return skuFromHtml;
    }

    return '';
  }

  getVariantImageUrl(variantId) {
    if (variantId == null) return null;

    const variantIdStr = String(variantId);

    if (this.variantGalleries?.[variantIdStr]?.length > 0) {
      return this.variantGalleries[variantIdStr][0].url;
    }

    const fallback = this.productMediaFallback;
    if (fallback?.length > 0) {
      const matches = fallback.filter(
        (img) => !img.variantId || String(img.variantId) === variantIdStr,
      );
      return (matches.length > 0 ? matches : fallback)[0]?.url || null;
    }

    return null;
  }

  setCardImage(src, alt) {
    const image = this.contentElement.querySelector('.cart-item-edit-image img');
    if (!image) return;
    if (src) image.src = src;
    if (alt != null) image.alt = alt;
  }

  appendClone(parent, node) {
    if (node) parent.appendChild(node.cloneNode(true));
  }

  appendVariantPicker(parent, productElement, hideDimensions) {
    const variantPicker = productElement.querySelector('variant-selects');
    if (!variantPicker) {
      if (hideDimensions) {
        productElement.querySelectorAll('product-dimensions').forEach((el) => el.remove());
      }
      return;
    }

    const clone = variantPicker.cloneNode(true);
    if (hideDimensions) {
      clone.querySelectorAll('product-dimensions').forEach((el) => el.remove());
    }
    parent.appendChild(clone);
  }

  preprocessHTML(productElement) {
    const cartItem = document.getElementById(`CartDrawer-Item-${this.currentLine}`);
    if (!cartItem) return null;

    this.cacheDimensionsData(productElement);

    const productElementCnt = document.createElement('product-info');

    const cartItemImage = cartItem.querySelector('.cart-item__image');
    const cartItemName = cartItem.querySelector('.cart-item__name');
    const cartItemSku = cartItem.querySelector('.cart-item__details .product__sku');
    const cartItemOptions = cartItem.querySelector('.cart-item__details dl');

    const itemInfo = document.createElement('div');
    const flexDiv = document.createElement('div');
    flexDiv.className = 'flex gap-4';

    if (cartItemImage) {
      const imageContainer = document.createElement('div');
      imageContainer.className = 'cart-item-edit-image';
      imageContainer.appendChild(cartItemImage.cloneNode(true));
      flexDiv.appendChild(imageContainer);
    }

    const detailsDiv = document.createElement('div');

    if (cartItemName) {
      const nameContainer = document.createElement('div');
      nameContainer.appendChild(cartItemName.cloneNode(true));
      detailsDiv.appendChild(nameContainer);
    }

    if (cartItemSku || cartItemOptions) {
      const optionContainer = document.createElement('div');
      optionContainer.className = 'cart-item-edit-option';

      if (cartItemSku) {
        const skuClone = cartItemSku.cloneNode(true);
        skuClone.dataset.cartEditModel = '';
        optionContainer.appendChild(skuClone);
      }

      if (cartItemOptions) {
        optionContainer.appendChild(cartItemOptions.cloneNode(true));
      }

      detailsDiv.appendChild(optionContainer);
    }

    flexDiv.appendChild(detailsDiv);
    itemInfo.appendChild(flexDiv);
    productElementCnt.appendChild(itemInfo);

    this.appendVariantPicker(productElementCnt, productElement, Boolean(cartItemOptions));

    const priceEl =
      productElement.querySelector('.pdp-dynamic-price') ||
      productElement.querySelector('[id^="price-"]') ||
      productElement.querySelector('.price__container');
    this.appendClone(productElementCnt, priceEl);

    const quantityBuyContainer = document.createElement('div');
    quantityBuyContainer.className = 'quantity-buy-wrapper';
    this.appendClone(quantityBuyContainer, productElement.querySelector('.product-form__quantity'));
    this.appendClone(quantityBuyContainer, productElement.querySelector('product-form'));

    if (quantityBuyContainer.childElementCount > 0) {
      productElementCnt.appendChild(quantityBuyContainer);
    }

    productElementCnt.className = productElement.className;
    productElementCnt.id = productElement.id;
    productElementCnt.setAttribute('data-update-url', 'false');
    if (productElement.dataset.section) {
      productElementCnt.setAttribute('data-section', productElement.dataset.section);
    }
    if (productElement.dataset.url) {
      productElementCnt.setAttribute('data-url', productElement.dataset.url);
    }

    return productElementCnt;
  }

  cacheDimensionsData(root) {
    const data = this.parseJsonFromScript(root, '[data-variants-dimensions]');
    if (data) this.dimensionsData = data;
  }

  setupVariantSync() {
    this.teardownVariantSync();

    const productInfo = this.contentElement.querySelector('product-info');
    const hasCartOptions = this.contentElement.querySelector('.cart-item-edit-option');
    if (!productInfo || !hasCartOptions) return;

    this.editSectionId = productInfo.dataset.section;
    this.stripProductDimensions();

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!this.classList.contains('active') || !data?.variant) return;
        if (data.sectionId && String(data.sectionId) !== String(this.editSectionId)) return;

        this.stripProductDimensions();
        this.updateProductCard(data.variant, data.html);
      });
    }

    this.dimensionsObserver = new MutationObserver(() => {
      if (this.contentElement.querySelector('.cart-item-edit-option')) {
        this.stripProductDimensions();
      }
    });
    this.dimensionsObserver.observe(productInfo, { childList: true, subtree: true });
  }

  teardownVariantSync() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
      this.variantChangeUnsubscriber = null;
    }
    if (this.dimensionsObserver) {
      this.dimensionsObserver.disconnect();
      this.dimensionsObserver = null;
    }
    this.editSectionId = null;
  }

  stripProductDimensions() {
    this.contentElement.querySelectorAll('product-dimensions').forEach((el) => el.remove());
  }

  formatDimension(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n % 1 === 0 ? Math.floor(n) : n;
  }

  formatSizeForCard(dimensions) {
    if (!dimensions) return '';
    return [
      [this.formatDimension(dimensions.width), 'W'],
      [this.formatDimension(dimensions.height), 'H'],
      [this.formatDimension(dimensions.thickness), 'D'],
    ]
      .filter(([value]) => value != null)
      .map(([value, label]) => `${value}" ${label}`)
      .join(' x ');
  }

  findOptionRow(dl, labelPattern) {
    return [...dl.querySelectorAll('.product-option')].find((row) => {
      const dt = row.querySelector('dt');
      if (!dt) return false;
      const label = dt.textContent.replace(':', '').trim();
      return labelPattern.test(label);
    });
  }

  syncCardOptionsFromPicker(dl) {
    const containers = this.contentElement.querySelectorAll(
      'variant-selects fieldset, variant-selects .product-form__input--dropdown',
    );

    containers.forEach((container) => {
      const labelEl =
        container.querySelector('.variant-label__title') || container.querySelector('label');
      if (!labelEl) return;

      const optName = labelEl.textContent.replace(':', '').trim();
      if (!optName || /^size$/i.test(optName)) return;

      const select = container.querySelector('select');
      const value =
        container.querySelector('[data-selected-value]')?.textContent?.trim() ||
        select?.options[select.selectedIndex]?.textContent?.trim();
      if (value) this.setCardOptionValue(dl, optName, value);
    });
  }

  setCardOptionValue(dl, optName, value) {
    const row = this.findOptionRow(
      dl,
      new RegExp(`^${optName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`, 'i'),
    );
    const dd = row?.querySelector('dd');
    if (dd) dd.textContent = value;
  }

  resolveVariantImageSrc(variant, html) {
    const mediaId = variant?.featured_media?.id;
    const imgFromHtml =
      html && mediaId ? html.querySelector(`[data-media-id="${mediaId}"] img`) : null;

    return (
      this.getVariantImageUrl(variant?.id) ||
      imgFromHtml?.currentSrc ||
      imgFromHtml?.src ||
      variant?.featured_image?.src ||
      variant?.featured_media?.preview_image?.src ||
      null
    );
  }

  updateModelSku(optionBlock, variant, html) {
    if (!optionBlock) return;

    const trimmed = this.resolveVariantSku(variant, html);
    let skuEl = optionBlock.querySelector('[data-cart-edit-model]');

    if (!trimmed) {
      if (skuEl) skuEl.hidden = true;
      return;
    }

    if (!skuEl) {
      skuEl = document.createElement('p');
      skuEl.className = 'product__sku';
      skuEl.dataset.cartEditModel = '';
      const dl = optionBlock.querySelector('dl');
      if (dl) {
        optionBlock.insertBefore(skuEl, dl);
      } else {
        optionBlock.prepend(skuEl);
      }
    }

    skuEl.hidden = false;
    const label = document.createElement('span');
    label.className = 'product__sku-label';
    label.textContent = 'Model # ';
    skuEl.replaceChildren(label, document.createTextNode(trimmed));
  }

  updateProductCard(variant, html) {
    const optionBlock = this.contentElement.querySelector('.cart-item-edit-option');
    this.updateModelSku(optionBlock, variant, html);

    const dl = optionBlock?.querySelector('dl');
    if (!variant) return;

    if (!dl) {
      this.setCardImage(this.resolveVariantImageSrc(variant, html), variant.featured_image?.alt);
      return;
    }

    const dimensions = this.dimensionsData?.[variant.id];
    const sizeRow = this.findOptionRow(dl, /^size$/i);
    const sizeDd = sizeRow?.querySelector('dd');

    if (dimensions) {
      if (sizeDd) {
        const sizeText = this.formatSizeForCard(dimensions);
        sizeDd.textContent = sizeText;
        sizeRow.hidden = !sizeText;
      }
    } else if (sizeRow) {
      if (sizeDd) sizeDd.textContent = '';
      sizeRow.hidden = true;
    }

    this.syncCardOptionsFromPicker(dl);
    this.setCardImage(this.resolveVariantImageSrc(variant, html), variant.featured_image?.alt);
  }

  setupUpdateButton() {
    const productForm = this.contentElement.querySelector('product-form');
    if (!productForm) return;

    const form = productForm.querySelector('form');
    if (!form) return;

    // Set current quantity from cart item
    const cartItem = document.getElementById(`CartDrawer-Item-${this.currentLine}`);
    if (cartItem) {
      const quantityInput = cartItem.querySelector('.quantity__input');
      if (quantityInput) {
        const currentQuantity = quantityInput.value;
        const editQuantityInput = this.contentElement.querySelector('.quantity__input');
        if (editQuantityInput) {
          editQuantityInput.value = currentQuantity;
          editQuantityInput.setAttribute('value', currentQuantity);
        }
      }
    }

    const submitButton = form.querySelector('.product-form__submit, button[type="submit"]');
    if (!submitButton) return;

    submitButton.addEventListener('click', (evt) => {
      evt.preventDefault();
      this.updateCartItem(form);
    });
    
  }

  updateCartItem(form) {
    const formData = new FormData(form);
    const newVariantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || 1);

    const submitButton = this.contentElement.querySelector('.product-form__submit');
    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
      const spinner = submitButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }

    // If variant changed, remove old line and add new variant
    // Otherwise, just update quantity
    if (newVariantId !== this.currentVariantId) {
      // Remove old line item
      const removeBody = JSON.stringify({
        id: this.currentVariantId,
        quantity: 0,
      });

      fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body: removeBody } })
        .then((response) => response.text())
        .then(() => {
          // Add new variant
          return this.addNewVariant(newVariantId, quantity);
        })
        .then(() => {
          this.handleUpdateSuccess({});
        })
        .catch((error) => {
          console.error('Error updating cart:', error);
          this.handleUpdateError();
        });
    } else {
      // Just update quantity
      const updateBody = JSON.stringify({
        line: this.currentLine,
        quantity: quantity,
      });

      fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body: updateBody } })
        .then((response) => response.text())
        .then(() => {
          this.handleUpdateSuccess({});
        })
        .catch((error) => {
          console.error('Error updating cart:', error);
          this.handleUpdateError();
        });
    }
  }

  addNewVariant(variantId, quantity) {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    config.body = formData;

    return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
  }

  handleUpdateSuccess(response) {
    // Close drawer first
    this.close();

    // Update cart drawer
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      // Use the same method as cart.js to update
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          
          // Update cart icon bubble
          const cartIconBubble = html.querySelector('#cart-icon-bubble');
          if (cartIconBubble) {
            const targetCartIcon = document.querySelector('#cart-icon-bubble');
            if (targetCartIcon) {
              targetCartIcon.replaceWith(cartIconBubble);
            }
          }
        })
        .catch((e) => {
          console.error('Error updating cart drawer:', e);
        });
    }

    // Publish cart update event
    if (window.PUB_SUB_EVENTS) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-edit-drawer',
        cartData: response,
      });
    }
  }

  handleUpdateError() {
    const submitButton = this.contentElement.querySelector('.product-form__submit');
    if (submitButton) {
      submitButton.removeAttribute('aria-disabled');
      submitButton.classList.remove('loading');
      const spinner = submitButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    }

    const errorWrapper = this.contentElement.querySelector('.product-form__error-message-wrapper');
    if (errorWrapper) {
      const errorMessage = errorWrapper.querySelector('.product-form__error-message');
      if (errorMessage) {
        errorMessage.textContent = window.cartStrings?.error || 'Error updating cart item';
      }
      errorWrapper.removeAttribute('hidden');
    }
  }

  close() {
    this.openRequestId += 1;

    if (this.loadAbortController) {
      this.loadAbortController.abort();
      this.loadAbortController = null;
    }

    this.teardownVariantSync();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.contentElement.replaceChildren();
    this.currentLine = null;
    this.currentVariantId = null;
    this.productUrl = null;
    this.dimensionsData = null;
    this.variantsSkuData = null;
    this.variantGalleries = null;
    this.productMediaFallback = null;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-edit-drawer', CartEditDrawer);

